<template>
  <div class="container">
    <div class="columns">
      <div class="column is-12">
        <p class="title mb-1">Rewards Dashboard</p>
        <p class="has-text-grey" style="margin-bottom: 20px">Manage and redeem your yield rewards</p>

        <div class="columns">
          <div class="column is-9">

            <!-- ERC4626 Stats Section -->
            <div class="columns mb-5">
              <div class="column is-12">
                <div class="box has-text-white has-background-dark">
                  <p class="mb-4 has-text-weight-bold">
                    <i class="ri-pie-chart-2-line is-size-3 mr-4 has-text-grey"></i>
                    ERC4626 Vault Statistics
                  </p>
                  
                  <div v-if="isLoadingErc4626Stats" class="has-text-centered">
                    <p>Loading ERC4626 statistics...</p>
                  </div>
                  
                  <div v-else class="columns">
                    
                    <div class="column is-4">
                      <p class="has-text-grey mb-2">Your Shares</p>
                      <p>
                        {{ erc4626Stats.userShares }}
                      </p>
                    </div>
                    
                    <div class="column is-4">
                      <p class="has-text-grey mb-2">Price Per Share</p>
                      <p>
                        {{ erc4626Stats.pricePerShare }}
                        <span class="has-text-weight-bold is-size-7 has-text-grey">CMTY</span>
                      </p>
                    </div>
                    
                    <div class="column is-4">
                      <p class="has-text-grey mb-2">Preview Redeem All</p>
                      <p>
                        {{ erc4626Stats.previewRedeemAll }}
                        <span class="has-text-weight-bold is-size-7 has-text-grey">CMTY</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <div class="column is-3">
            <div class="box has-text-centered" style="height: 159px;">
              <img src="@/assets/nft.png" alt="NFT Icon" style="max-height: 4rem;" />
              <p class="has-text-weight-bold mt-2">NFT ID: #{{ erc4626Stats.userNftId }}</p>
            </div>
          </div>
        </div>
        
        <!-- Yield Redemption Section -->
        <div class="columns mb-5">
          <div class="column is-6">
            <div class="box">
              <div class="is-flex is-align-items-center mb-4">
                <img src="@/assets/yield.png" alt="Yield Icon" class="mr-4" style="max-height: 4rem;" />
                <p class="has-text-weight-bold is-size-5">Yield Redemption</p>
              </div>
              
              <div v-if="isLoadingErc4626Stats" class="has-text-centered">
                <p>Loading redemption data...</p>
              </div>
              
              <div v-else>
                <div class="field">
                  <p class="has-text-grey is-size-7 mb-1">Redeem Shares</p>
                  <div class="control">
                    <input 
                      class="input" 
                      type="number" 
                      step="0.000001" 
                      min="0" 
                      :max="parseFloat(erc4626Stats.userShares)" 
                      v-model="partialRedeemAmount"
                      placeholder="Enter amount of shares to redeem"
                      :disabled="parseFloat(erc4626Stats.userShares) <= 0 || isRedeeming"
                    >
                  </div>
                  <p class="help">Maximum: {{ erc4626Stats.userShares }} shares</p>
                </div>
                
                <div class="field">
                  <p class="has-text-grey is-size-7 mb-1">Expected Return</p>
                  <div class="control">
                    <input 
                      class="input is-static" 
                      type="text" 
                      readonly 
                      :value="calculateExpectedReturn() + ' CMTY'"
                    >
                  </div>
                  <p class="help">Based on current price per share: {{ erc4626Stats.pricePerShare }} CMTY</p>
                </div>
                
                <div class="field is-grouped mt-4">
                  <div class="control">
                    <button 
                      class="button is-dark" 
                      :disabled="!isValidPartialRedemption || isRedeeming" 
                      @click="redeemPartial"
                    >
                      <span v-if="isRedeeming">
                        <i class="ri-loader-4-line ri-spin"></i>
                        Redeeming...
                      </span>
                      <span v-else>
                        Redeem Shares
                      </span>
                    </button>
                  </div>
                  <div class="control ml-2">
                    <button 
                      class="button is-dark" 
                      :disabled="parseFloat(erc4626Stats.previewRedeemAll) <= 0 || isRedeeming" 
                      @click="showRedeemConfirmModal = true"
                    >
                      Redeem All Shares
                    </button>
                  </div>
                </div>
                
                <!-- Los mensajes ahora se mostrarán con toast fire -->
              </div>
            </div>
          </div>
          
          <div class="column is-6">
            <div class="box">
              <div class="is-flex is-align-items-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#3273dc" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mr-4" style="max-height: 4rem;">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 16v-4"></path>
                  <path d="M12 8h.01"></path>
                </svg>
                <p class="has-text-weight-bold is-size-5">About Yield Redemption</p>
              </div>
              
              <div class="content is-size-7">
                <p>You can redeem your shares to receive the underlying CMTY tokens plus any accumulated yield.</p>
                
                <div class="columns mt-4">
                  <div class="column is-6">
                    <p class="has-text-grey mb-1">Current Price Per Share</p>
                    <p class="has-text-weight-bold">
                      {{ erc4626Stats.pricePerShare }}
                      <span class="has-text-weight-bold is-size-7 has-text-grey">CMTY</span>
                    </p>
                  </div>
                  
                  <div class="column is-6">
                    <p class="has-text-grey mb-1">Your Shares Value</p>
                    <p class="has-text-weight-bold">
                      {{ erc4626Stats.previewRedeemAll }}
                      <span class="has-text-weight-bold is-size-7 has-text-grey">CMTY</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="columns">
          <!-- User Stats Section -->
          
          <!-- Redemption History Section -->
          <div class="column is-12">
            <div class="box">
              <h2 class="mb-6 has-text-weight-bold">Redemption History</h2>
              
              <div v-if="isLoadingHistory" class="has-text-centered">
                <p>Loading redemption history...</p>
              </div>
              
              <div v-else-if="redemptionEvents.length === 0" class="has-text-centered">
                <p>No redemption events found</p>
              </div>
              
              <div v-else>
                <table class="table is-fullwidth">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>NFT ID</th>
                      <th>Type</th>
                      <th>Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(event, index) in redemptionEvents" :key="index">
                      <td>
                        <div class="tags has-addons">
                          <span class="tag is-dark">{{ formatDate(event.timestamp).date }}</span>
                          <span class="tag is-info">{{ formatDate(event.timestamp).time }}</span>
                        </div>
                      </td>
                      <td>
                        <div class="tags has-addons">
                          <span class="tag is-dark">CMTY</span>
                          <span class="tag is-info">{{ formatAmount(event.tokens) }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="tag is-info">#{{ event.nftId }}</span>
                      </td>
                      <td>
                        <span class="tag is-info">{{ formatRedemptionType(event.cyclosId) }}</span>
                      </td>
                      <td>
                        <a :href="getExplorerUrl(event.transactionHash)" target="_blank" class="is-size-7">
                          {{ shortenAddress(event.transactionHash) }}
                          <i class="ri-external-link-line ml-1"></i>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Redeem Modal -->
    <div class="modal" :class="{'is-active': showRedeemModal}">
      <div class="modal-background" @click="showRedeemModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Redeem Tokens</p>
          <button class="delete is-dark" aria-label="close" @click="showRedeemModal = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="field">
            <label class="label">Amount to Redeem</label>
            <div class="control">
              <input class="input" type="number" placeholder="Amount" v-model="redeemAmount">
            </div>
            <p class="help">Maximum: {{ formatAmount(userStats.availableToRedeem) }}</p>
          </div>
          
          <div class="field">
            <div class="control">
              <button 
                class="button is-dark is-fullwidth" 
                @click="setMaxRedeemAmount"
              >
                Use Maximum
              </button>
            </div>
          </div>
          
          <!-- Mensajes ahora se muestran con toast fire -->
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-black" 
            @click="redeemTokens"
            :disabled="!redeemAmount || isRedeeming || redeemAmount > userStats.availableToRedeem"
          >
            Confirm Redemption
          </button>
          <button class="button is-light" @click="showRedeemModal = false">Close</button>
        </footer>
      </div>
    </div>
    
    <!-- Redeem Options Modal -->
    <div class="modal" :class="{'is-active': showRedeemOptions}">
      <div class="modal-background" @click="showRedeemOptions = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Redeem Shares</p>
          <button class="delete is-dark" aria-label="close" @click="showRedeemOptions = false"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="redeemMessage" class="notification" :class="redeemSuccess ? 'is-success' : 'is-danger'">
            {{ redeemMessage }}
          </div>
          
          <div v-if="!redeemSuccess">
            <p class="mb-4">Choose how many shares you want to redeem:</p>
            
            <div class="field">
              <label class="label">Available to Redeem</label>
              <div class="control">
                <input class="input is-static" type="text" readonly :value="erc4626Stats.previewRedeemAll + ' CMTY'">
              </div>
            </div>
            
            <div class="field">
              <label class="label">Redemption Amount (CMTY)</label>
              <div class="control has-icons-right">
                <input 
                  class="input" 
                  type="number" 
                  step="0.000001" 
                  min="0" 
                  :max="parseFloat(erc4626Stats.previewRedeemAll)" 
                  v-model="partialRedeemAmount"
                  placeholder="Enter amount to redeem"
                >
                <span class="icon is-small is-right">
                  <i class="ri-coin-line"></i>
                </span>
              </div>
              <p class="help">
                <a @click="setMaxRedeemAmount()" class="is-link">Use max</a>
              </p>
            </div>
            
            <div class="field">
              <div class="control">
                <label class="radio">
                  <input 
                    type="radio" 
                    name="redemption-type" 
                    v-model="redemptionType" 
                    value="partial"
                    :disabled="!partialRedeemAmount || parseFloat(partialRedeemAmount) <= 0"
                  >
                  Partial Redemption
                </label>
                <label class="radio ml-4">
                  <input 
                    type="radio" 
                    name="redemption-type" 
                    v-model="redemptionType" 
                    value="all"
                  >
                  Redeem All Shares
                </label>
              </div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-black" 
            @click="processRedemption"
            :disabled="isRedeeming || redeemSuccess || !isValidRedemption"
          >
            <span v-if="isRedeeming">
              <i class="ri-loader-4-line ri-spin"></i>
              Processing...
            </span>
            <span v-else>
              Confirm Redemption
            </span>
          </button>
          <button class="button is-light" @click="showRedeemOptions = false">Cancel</button>
        </footer>
      </div>
    </div>
    
    <!-- Redeem All Confirmation Modal -->
    <div class="modal" :class="{'is-active': showRedeemConfirmModal}">
      <div class="modal-background" @click="showRedeemConfirmModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Redemption</p>
          <button class="delete is-dark" aria-label="close" @click="showRedeemConfirmModal = false"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="redeemMessage" class="notification" :class="redeemSuccess ? 'is-success' : 'is-danger'">
            {{ redeemMessage }}
          </div>
          
          <div v-if="!redeemSuccess">
            <p class="mb-4">Are you sure you want to redeem all your shares?</p>
            
            <div class="field">
              <label class="label">Amount to Redeem</label>
              <div class="control">
                <input class="input is-static" type="text" readonly :value="erc4626Stats.previewRedeemAll + ' CMTY'">
              </div>
              <p class="help">This is the estimated amount you will receive based on current share price.</p>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button 
            class="button is-black" 
            @click="redeemAll"
            :disabled="isRedeeming || redeemSuccess"
          >
            <span v-if="isRedeeming">
              <i class="ri-loader-4-line ri-spin"></i>
              Processing...
            </span>
            <span v-else>
              Confirm Redemption
            </span>
          </button>
          <button class="button is-light" @click="showRedeemConfirmModal = false">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import Web3 from 'web3';
import { mapState } from 'vuex';
import * as util from "@/utilities/util.js";

export default {
  name: 'RewardsPage',
  beforeRouteEnter(to, from, next) {
    const store = window.store;
    next('/'); // Redirect to home if wallet is not connected
    return;
  },
  data() {
    return {
      isLoading: true,
      isLoadingHistory: true,
      isLoadingErc4626Stats: true,
      isRedeeming: false,
      showRedeemModal: false,
      showRedeemConfirmModal: false,
      showRedeemOptions: false,
      redeemAmount: '',
      partialRedeemAmount: '',
      redemptionType: 'all',
      redeemMessage: '',
      redeemSuccess: false,
      userStats: {
        tokensInVault: '0',
        shares: '0',
        availableToRedeem: '0'
      },
      erc4626Stats: {
        userNftId: '0',
        rawUserShares: '0', // New property to store raw user shares
        userShares: '0',
        pricePerShare: '0',
        totalAssets: '0',
        totalShares: '0',
        previewRedeemAll: '0',
        maxRedeem: '0',
        isManager: false
      },
      redemptionEvents: [],
      web3: null,
      vaultContract: null,
      tokenContract: null,
      orchestratorContract: null,
      nftContract: null,
      networkId: null
    };
  },
  computed: {
    ...mapState(['account']),
    canRedeem() {
      return this.userStats.availableToRedeem > 0;
    },
    isValidRedemption() {
      if (this.redemptionType === 'all') {
        return parseFloat(this.erc4626Stats.previewRedeemAll) > 0;
      } else if (this.redemptionType === 'partial') {
        return this.partialRedeemAmount && 
               parseFloat(this.partialRedeemAmount) > 0 && 
               parseFloat(this.partialRedeemAmount) <= parseFloat(this.erc4626Stats.previewRedeemAll);
      }
      return false;
    },
    isValidPartialRedemption() {
      // Verificar si hay un valor ingresado
      if (!this.partialRedeemAmount) return false;
      
      // Convertir el valor ingresado a un número
      const inputAmount = parseFloat(this.partialRedeemAmount);
      
      // Obtener el valor máximo de shares (eliminando posibles comas de formato)
      const maxShares = parseFloat(String(this.erc4626Stats.rawUserShares).replace(/,/g, '')); // Use rawUserShares instead of userShares
      
      console.log('Input amount:', inputAmount, 'Max shares:', maxShares);
      
      // Verificar que el valor sea válido y esté dentro del rango permitido
      return !isNaN(inputAmount) && inputAmount > 0 && inputAmount <= maxShares;
    }
  },
  async mounted() {
    try {
      if (this.account) {
        await this.initWeb3();
        if (this.web3 && this.orchestratorContract) {
          // Load all data in parallel for better performance
          await Promise.all([
            this.loadUserStats(),
            this.loadRedemptionEvents(),
            this.loadErc4626Stats()
          ]);
        } else {
          console.log('Web3 or contracts not initialized correctly');
          this.isLoading = false;
          this.isLoadingHistory = false;
          this.isLoadingErc4626Stats = false;
        }
      } else {
        console.log('No account connected');
        this.isLoading = false;
        this.isLoadingHistory = false;
        this.isLoadingErc4626Stats = false;
      }
    } catch (error) {
      console.error('Error in mounted:', error);
      this.isLoading = false;
      this.isLoadingHistory = false;
      this.isLoadingErc4626Stats = false;
    }
  },
  watch: {
    account(newAccount) {
      try {
        if (newAccount) {
          this.initWeb3().then(() => {
            if (this.web3 && this.orchestratorContract) {
              // Load all data in parallel for better performance
              Promise.all([
                this.loadUserStats(),
                this.loadRedemptionEvents(),
                this.loadErc4626Stats()
              ]);
            }
          });
        } else {
          // Reset all user data when account is disconnected
          this.userStats = {
            tokensInVault: '0',
            shares: '0',
            availableToRedeem: '0'
          };
          this.erc4626Stats = {
            userNftId: '0',
            rawUserShares: '0', // New property to store raw user shares
            userShares: '0',
            pricePerShare: '0',
            totalAssets: '0',
            totalShares: '0',
            previewRedeemAll: '0',
            maxRedeem: '0',
            isManager: false
          };
          this.redemptionEvents = [];
        }
      } catch (error) {
        console.error('Error in watch account:', error);
        this.isLoading = false;
        this.isLoadingHistory = false;
        this.isLoadingErc4626Stats = false;
      }
    }
  },
  methods: {
    async redeemAll() {
      this.isRedeeming = true;
      
      try {
        if (!this.account) {
          throw new Error('No account connected');
        }
        
        if (parseFloat(this.erc4626Stats.previewRedeemAll) <= 0) {
          throw new Error('No shares available to redeem');
        }
        
        console.log('Calling redeemAll on Orchestrator contract...');
        // Call redeemAll on the Orchestrator contract
        await this.orchestratorContract.methods.redeemAll().send({ from: this.account });
        
        // Success - use toast fire instead of message div
        util.toast.fire({ 
          icon: 'success', 
          title: `Successfully redeemed ${this.erc4626Stats.previewRedeemAll} CMTY tokens` 
        });
        
        // Reload data
        await this.loadErc4626Stats();
        await this.loadRedemptionEvents();
        
        // Close modal after successful redemption
        setTimeout(() => {
          this.showRedeemConfirmModal = false;
        }, 2000);
      } catch (error) {
        console.error('Error redeeming all shares:', error);
        // Use toast fire for error messages instead of message div
        util.toast.fire({ 
          icon: 'error', 
          title: error.message || 'Error redeeming shares. Please try again.' 
        });
      } finally {
        this.isRedeeming = false;
      }
    },
    
    async initWeb3() {
      try {
        // Initialize web3
        const util = require('@/utilities/util.js');
        
        // Verificar si web3 está disponible en el store
        if (this.$store && this.$store.state && this.$store.state.web3) {
          console.log('Using web3 from store');
          this.web3 = this.$store.state.web3;
        } else {
          console.log('Getting new web3 instance');
          this.web3 = await util.getWeb3();
        }
        
        // Initialize contracts using utility functions
        // this.vaultContract = await util.vaultContractInstance();
        this.tokenContract = await util.tokenContractInstance();
        this.orchestratorContract = await util.OrchestratorContractInstance();
        this.nftContract = await util.NFTContractInstance();
        
        // Get network ID
        this.networkId = await this.web3.eth.net.getId();
        console.log('Connected to network ID:', this.networkId);
        
        return true;
      } catch (error) {
        console.error('Error initializing web3:', error);
        return false;
      }
    },
    
    async loadErc4626Stats() {
      this.isLoadingErc4626Stats = true;
      
      try {
        if (!this.orchestratorContract || !this.nftContract) {
          await this.initWeb3();
        }
        
        if (!this.account) {
          console.log('No account connected');
          this.isLoadingErc4626Stats = false;
          return;
        }
        
        // 1. Get user's NFT ID
        const userNftId = await this.nftContract.methods.getUserToken(this.account).call();
        this.erc4626Stats.userNftId = userNftId.toString();
        
        // 2. Get user's shares in the Orchestrator
        const userShares = await this.orchestratorContract.methods.userShares(this.account).call();
        this.erc4626Stats.rawUserShares = userShares; // Store the raw value
        this.erc4626Stats.userShares = this.formatAmount(userShares);
        
        // 3. Get the global price per share
        const pricePerShare = await this.orchestratorContract.methods.pricePerShare().call();
        this.erc4626Stats.pricePerShare = this.formatAmount(pricePerShare);
        
        // 4. Get total assets in the system
        const totalAssets = await this.orchestratorContract.methods.totalAssets().call();
        this.erc4626Stats.totalAssets = this.formatAmount(totalAssets);
        
        // 5. Get total shares in the system
        const totalShares = await this.orchestratorContract.methods.totalShares().call();
        this.erc4626Stats.totalShares = this.formatAmount(totalShares);
        
        // 6. Preview redeem all shares
        if (userShares && userShares !== '0') {
          const previewRedeemAll = await this.orchestratorContract.methods.previewRedeemAll(this.account).call();
          this.erc4626Stats.previewRedeemAll = this.formatAmount(previewRedeemAll);
          
          // 7. Max redeem amount
          const maxRedeem = await this.orchestratorContract.methods.maxRedeem(this.account).call();
          this.erc4626Stats.maxRedeem = this.formatAmount(maxRedeem);
        }
        
        // 8. Check if user has MANAGER_ROLE
        try {
          // Use the constant value of MANAGER_ROLE (keccak256('MANAGER_ROLE'))
          const MANAGER_ROLE = '0x241ecf16d79d0f8dbfb92cbc07fe17840425976cf0667f022fe9877caa831b08';
          
          // Create a custom ABI for AccessControl's hasRole function
          const accessControlABI = [
            {
              "inputs": [
                {
                  "internalType": "bytes32",
                  "name": "role",
                  "type": "bytes32"
                },
                {
                  "internalType": "address",
                  "name": "account",
                  "type": "address"
                }
              ],
              "name": "hasRole",
              "outputs": [
                {
                  "internalType": "bool",
                  "name": "",
                  "type": "bool"
                }
              ],
              "stateMutability": "view",
              "type": "function"
            }
          ];
          
          // Get the vault contract address
          // const util = require('@/utilities/util.js');
          // const vaultAddress = util.CMTYv_TokenContractAddress;
          
          // Create a contract instance with just the hasRole function
          // const roleCheckContract = new this.web3.eth.Contract(accessControlABI, vaultAddress);
          // this.erc4626Stats.isManager = await roleCheckContract.methods.hasRole(MANAGER_ROLE, this.account).call();
          // console.log('Is manager:', this.erc4626Stats.isManager);
        } catch (error) {
          console.error('Error checking manager role:', error);
          this.erc4626Stats.isManager = false;
        }
        
      } catch (error) {
        console.error('Error loading ERC4626 stats:', error);
      } finally {
        this.isLoadingErc4626Stats = false;
      }
    },
    
    async loadUserStats() {
      this.isLoading = true;
      try {
        if (!this.tokenContract) {
          await this.initWeb3();
        }
        
        // Get user's balance of shares
        // const shares = await this.vaultContract.methods.balanceOf(this.account).call();
        
        // Get the amount of tokens the shares are worth
        // const tokensInVault = await this.vaultContract.methods.convertToAssets(shares).call();
        
        // Get maximum amount user can redeem
        // const maxRedeem = await this.vaultContract.methods.maxRedeem(this.account).call();
        
        /*
        this.userStats = {
          tokensInVault,
          shares,
          availableToRedeem: maxRedeem
        };
        */
      } catch (error) {
        console.error('Error loading user stats:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadRedemptionEvents() {
      this.isLoadingHistory = true;
      try {
        if (!this.orchestratorContract) {
          await this.initWeb3();
        }
        
        // Get current block number
        const currentBlock = await this.web3.eth.getBlockNumber();
        
        // Calculate block range (last ~100000 blocks, approximately 10 days)
        const fromBlock = Math.max(0, currentBlock - 100000);
        
        console.log('Searching for Withdraw events from Orchestrator from block', fromBlock, 'to', currentBlock);
        
        // Get Withdraw events from the Orchestrator contract
        const events = await this.orchestratorContract.getPastEvents('Withdraw', {
          fromBlock: fromBlock,
          toBlock: 'latest',
          filter: { user: this.account } // Filter for events related to the current user
        });
        
        console.log('Found', events.length, 'Withdraw events from Orchestrator');
        
        if (events.length > 0) {
          // Process events
          const processedEvents = await Promise.all(events.map(async (event) => {
            const block = await this.web3.eth.getBlock(event.blockNumber);
            return {
              // In Orchestrator, the event has different parameters
              tokens: event.returnValues.amount, // Amount of tokens withdrawn
              nftId: event.returnValues.nftId,   // NFT ID associated with the withdrawal
              cyclosId: event.returnValues.cyclosId, // Type of withdrawal (yield_redemption, yield_redemption_all, etc.)
              user: event.returnValues.user,     // User address
              transactionHash: event.transactionHash,
              blockNumber: event.blockNumber,
              timestamp: block.timestamp * 1000  // Convert to milliseconds
            };
          }));
          
          // Sort by timestamp (newest first) and limit to 50
          this.redemptionEvents = processedEvents
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 50);
        } else {
          // If no events found, show empty array
          this.redemptionEvents = [];
          console.log('No Withdraw events found in the specified block range');
        }
      } catch (error) {
        console.error('Error loading redemption events:', error);
        this.redemptionEvents = [];
      } finally {
        this.isLoadingHistory = false;
      }
    },
    
    calculateExpectedReturn() {
      if (!this.partialRedeemAmount || isNaN(this.partialRedeemAmount) || parseFloat(this.partialRedeemAmount) <= 0) {
        return '0';
      }
      
      // Calculate expected return based on price per share
      const shares = parseFloat(this.partialRedeemAmount);
      const pricePerShare = parseFloat(this.erc4626Stats.pricePerShare);
      const expectedReturn = shares * pricePerShare;
      
      // Format to 6 decimal places
      return expectedReturn.toFixed(6);
    },
    
    setMaxRedeemAmount() {
      this.partialRedeemAmount = this.erc4626Stats.rawUserShares; // Use rawUserShares instead of userShares
    },
    
    formatAmount(amount) {
      if (!amount) return '0';
      // Convert from wei to ether and format with 2 decimal places and thousands separator
      const valueInEther = parseFloat(amount) / 10**18;
      return valueInEther.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    
    formatDate(timestamp) {
      if (!timestamp) return { date: '', time: '' };
      const date = new Date(timestamp);
      
      // Format date as MM/DD/YYYY
      const dateStr = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      // Format time as HH:MM:SS AM/PM
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      
      return { date: dateStr, time: timeStr };
    },
    
    shortenAddress(address) {
      if (!address) return '';
      return address.substring(0, 6) + '...' + address.substring(address.length - 4);
    },
    
    getExplorerUrl(txHash) {
      // Replace with the appropriate explorer URL for your network
      return `https://scan.v4.testnet.pulsechain.com/#/tx/${txHash}`;
    }
  }
};
</script>

<style scoped>
.input.is-static {
  background-color: #f5f5f5;
  border-color: #f5f5f5;
  box-shadow: none;
  padding-left: 0.75em;
}
</style>
